import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import axiosInstance from "../axiosInstance";
import { API_ENDPOINTS } from "../config";
import Swal from "sweetalert2";

import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { getAuthToken } from "../utils/session";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";

const FONT_FAMILY =
  'Inter, Manrope, "SF Pro Display", "Segoe UI", Roboto, Arial, sans-serif';

const COLORS = {
  page: "#F7F9FC",
  pageSoft: "#FFFFFF",
  surface: "#FFFFFF",
  surfaceSoft: "#FBFCFE",
  surfaceStrong: "#FFFFFF",

  border: "rgba(15,36,56,0.10)",
  borderSoft: "rgba(15,36,56,0.07)",
  borderStrong: "rgba(15,36,56,0.16)",

  text: "#172033",
  textStrong: "#102840",
  muted: "#64748B",
  mutedSoft: "#94A3B8",

  primary: "#1E4B81",
  primaryDark: "#102840",
  primarySoft: "#EEF5FF",

  accent: "#D37D36",
  accentDark: "#B6632C",
  accentSoft: "#FFF4EA",

  success: "#14A67A",
  successSoft: "#EAFBF6",

  danger: "#D32F2F",
  dangerSoft: "#FFEBEE",

  shadow: "0 18px 48px rgba(15,36,56,0.06)",
  hoverShadow: "0 18px 42px rgba(15,36,56,0.08)",

  background:
    "radial-gradient(circle at 18% 0%, rgba(30,75,129,0.08), transparent 32%), radial-gradient(circle at 92% 8%, rgba(211,125,54,0.08), transparent 28%), linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 48%, #F7F9FC 100%)",
};

const TYPO = {
  pageTitle: 900,
  sectionTitle: 820,
  itemTitle: 760,
  selectedItem: 820,
  bodyStrong: 680,
  body: 520,
  meta: 650,
  chip: 800,
  button: 820,
};

const NIVEL_OPTIONS = ["Todos los niveles", "1", "2", "3", "4", "5"];

const cardSx = {
  bgcolor: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: "22px",
  boxShadow: COLORS.shadow,
};

const getQuestionKey = (pregunta) => pregunta?.pregunta;

const getTotalQuestions = (cuestionario) => {
  if (!cuestionario) return 0;

  if (Array.isArray(cuestionario.preguntasRespuestas)) {
    return cuestionario.preguntasRespuestas.length;
  }

  return [
    ...(cuestionario.preguntasGenerales || []),
    ...(cuestionario.preguntasPorProceso || []),
  ].length;
};

const primaryButtonSx = {
  minHeight: 40,
  borderRadius: "13px",
  px: 2,
  textTransform: "none",
  fontWeight: TYPO.button,
  fontSize: "0.76rem",
  fontFamily: FONT_FAMILY,
  color: "#FFFFFF",
  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
  boxShadow: "0 12px 28px rgba(30,75,129,0.16)",

  "&:hover": {
    background: `linear-gradient(135deg, ${COLORS.primaryDark}, #0B1F33)`,
    boxShadow: "0 16px 34px rgba(30,75,129,0.20)",
  },

  "&.Mui-disabled": {
    bgcolor: "rgba(100,116,139,0.16)",
    background: "rgba(100,116,139,0.16)",
    color: "rgba(100,116,139,0.78)",
    boxShadow: "none",
  },
};

const secondaryButtonSx = {
  minHeight: 40,
  borderRadius: "13px",
  px: 1.7,
  textTransform: "none",
  fontWeight: TYPO.button,
  fontSize: "0.76rem",
  fontFamily: FONT_FAMILY,
  color: COLORS.primary,
  borderColor: "rgba(30,75,129,0.22)",
  bgcolor: COLORS.surface,

  "&:hover": {
    borderColor: COLORS.primary,
    bgcolor: COLORS.primarySoft,
  },
};

const CuestionarioPage = () => {
  const { cuestionarioId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isCompleted = new URLSearchParams(location.search).get("completed") === "true";
  const autoevaluacionId = cuestionarioId;

  const [cuestionario, setCuestionario] = useState(null);
  const [procesos, setProcesos] = useState([]);
  const [procesosFiltrados, setProcesosFiltrados] = useState([]);
  const [procesoSeleccionado, setProcesoSeleccionado] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [progreso, setProgreso] = useState(0);
  const [nivelSeleccionado, setNivelSeleccionado] = useState("Todos los niveles");
  const [respuestasSeleccionadas, setRespuestasSeleccionadas] = useState({});
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [ayudaSeleccionada, setAyudaSeleccionada] = useState("");
  const [isLoadingFinalizar, setIsLoadingFinalizar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchProceso, setSearchProceso] = useState("");
  const [savingQuestionId, setSavingQuestionId] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  useEffect(() => {
    document.body.style.background = COLORS.page;
  }, []);

  useEffect(() => {
    const fetchCuestionario = async () => {
      try {
        setLoading(true);
        setError("");

        const token = getAuthToken();
        const config = { headers: { Authorization: `Bearer ${token}` } };

        const response = await axiosInstance.get(
          API_ENDPOINTS.autoevaluaciones.obtenerOptimizado(autoevaluacionId),
          config
        );

        const cuestionarioData = response.data.cuestionario;

        setCuestionario(cuestionarioData);
        setProgreso(cuestionarioData.progreso || 0);

        const initialRespuestas = {};

        [
          ...(cuestionarioData.preguntasGenerales || []),
          ...(cuestionarioData.preguntasPorProceso || []),
        ].forEach((pregunta) => {
          if (pregunta.respuestaSeleccionada) {
            initialRespuestas[pregunta.pregunta] = pregunta.respuestaSeleccionada;
          }
        });

        setRespuestasSeleccionadas(initialRespuestas);

        const preguntasGeneralesPorNivel = (cuestionarioData.preguntasGenerales || []).reduce(
          (acc, pregunta) => {
            const nivel = pregunta.general || "Sin nivel";

            if (!acc[nivel]) {
              acc[nivel] = {
                proceso: {
                  _id: `general-${nivel}`,
                  nombre: `Preguntas generales nivel ${nivel}`,
                  abreviatura: `General N${nivel}`,
                  nivel,
                  tipo: "General",
                },
                preguntas: [],
              };
            }

            acc[nivel].preguntas.push(pregunta);
            return acc;
          },
          {}
        );

        const procesosMap = (cuestionarioData.preguntasPorProceso || []).reduce(
          (acc, pregunta) => {
            const procesoId = pregunta.proceso?._id;
            if (!procesoId) return acc;

            if (!acc[procesoId]) {
              acc[procesoId] = {
                proceso: pregunta.proceso,
                preguntas: [],
              };
            }

            acc[procesoId].preguntas.push(pregunta);
            return acc;
          },
          {}
        );

        const procesosList = [
          ...Object.values(procesosMap),
          ...Object.values(preguntasGeneralesPorNivel),
        ];

        setProcesos(procesosList);
        setProcesosFiltrados(procesosList);

        if (procesosList.length > 0) {
          const first = procesosList[0];
          setProcesoSeleccionado(first.proceso._id);
          setPreguntas(first.preguntas || []);
        }
      } catch (error) {
        console.error("Error al cargar el cuestionario:", error);
        setError("No hemos podido cargar el cuestionario. Inténtalo de nuevo más tarde.");
      } finally {
        setLoading(false);
      }
    };

    fetchCuestionario();
  }, [autoevaluacionId]);

  useEffect(() => {
    let filtrados = [...procesos];

    if (nivelSeleccionado !== "Todos los niveles") {
      const nivelInt = parseInt(nivelSeleccionado, 10);

      filtrados = filtrados.filter((p) => {
        const procesoNivel = p.proceso.nivel;

        return (
          procesoNivel === nivelInt ||
          String(p.proceso.nombre || "").includes(`Nivel ${nivelSeleccionado}`)
        );
      });
    }

    if (searchProceso.trim()) {
      const normalized = searchProceso.trim().toLowerCase();

      filtrados = filtrados.filter((p) => {
        const nombre = p.proceso.nombre || "";
        const abreviatura = p.proceso.abreviatura || "";
        const tipo = p.proceso.tipo || "";

        return `${nombre} ${abreviatura} ${tipo}`.toLowerCase().includes(normalized);
      });
    }

    setProcesosFiltrados(filtrados);

    const existsSelected = filtrados.some((p) => p.proceso._id === procesoSeleccionado);

    if (!existsSelected && filtrados.length > 0) {
      const first = filtrados[0];
      setProcesoSeleccionado(first.proceso._id);
      setPreguntas(first.preguntas || []);
    }

    if (filtrados.length === 0) {
      setProcesoSeleccionado(null);
      setPreguntas([]);
    }
  }, [nivelSeleccionado, searchProceso, procesos, procesoSeleccionado]);

  const seleccionarProceso = (procesoId, listaProcesos = procesosFiltrados) => {
    setProcesoSeleccionado(procesoId);

    const proceso = listaProcesos.find((p) => p.proceso._id === procesoId);
    setPreguntas(proceso?.preguntas || []);
  };

  const manejarSeleccionRespuesta = async (preguntaId, respuestaId) => {
    if (isCompleted) return;

    setRespuestasSeleccionadas((prev) => ({
      ...prev,
      [preguntaId]: respuestaId,
    }));

    try {
      setSavingQuestionId(preguntaId);

      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const payload = {
        preguntaId,
        respuesta: respuestaId,
      };

      const response = await axiosInstance.put(
        API_ENDPOINTS.autoevaluaciones.actualizar(cuestionario._id),
        payload,
        config
      );

      setProgreso(response.data.cuestionario.progreso);
      setCuestionario(response.data.cuestionario);
      setLastSavedAt(new Date());
    } catch (error) {
      Swal.fire({
        title: "No se pudo guardar la respuesta",
        text: "Revisa la conexión e inténtalo de nuevo.",
        icon: "error",
        confirmButtonColor: COLORS.danger,
      });
    } finally {
      setSavingQuestionId(null);
    }
  };

  const abrirDialogoAyuda = (ayuda) => {
    setAyudaSeleccionada(ayuda);
    setDialogoAbierto(true);
  };

  const cerrarDialogoAyuda = () => {
    setDialogoAbierto(false);
    setAyudaSeleccionada("");
  };

  const finalizarCuestionario = async () => {
    if (isCompleted) {
      navigate("/autoevaluaciones");
      return;
    }

    const totalPreguntas = getTotalQuestions(cuestionario);
    const respondidas = Object.keys(respuestasSeleccionadas).length;
    const incompleto = respondidas < totalPreguntas || totalPreguntas === 0;

    const result = await Swal.fire({
      title: incompleto ? "¿Guardar y salir?" : "¿Finalizar el cuestionario?",
      text: incompleto
        ? "Aún no has completado todas las preguntas. Puedes guardar el avance y continuar más tarde."
        : "Has respondido todas las preguntas. Se cerrará la evaluación y se generarán los resultados.",
      icon: incompleto ? "info" : "question",
      showCancelButton: true,
      confirmButtonText: incompleto ? "Guardar y salir" : "Finalizar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: incompleto ? COLORS.primary : COLORS.accent,
      cancelButtonColor: COLORS.muted,
    });

    if (!result.isConfirmed) return;

    try {
      setIsLoadingFinalizar(true);

      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const payload = {
        respuestas:
          cuestionario.preguntasRespuestas?.map((pr) => ({
            pregunta: pr.pregunta,
            respuestaSeleccionada: respuestasSeleccionadas[pr.pregunta] || null,
          })) || [],
      };

      const endpoint = incompleto
        ? API_ENDPOINTS.autoevaluaciones.guardarProgreso(cuestionario._id)
        : API_ENDPOINTS.autoevaluaciones.finalizar(cuestionario._id);

      await axiosInstance.post(endpoint, payload, config);

      navigate("/autoevaluaciones");
    } catch (error) {
      Swal.fire(
        "Error",
        incompleto ? "No se pudo guardar el progreso." : "No se pudo finalizar el cuestionario.",
        "error"
      );
    } finally {
      setIsLoadingFinalizar(false);
    }
  };

  const totalPreguntas = getTotalQuestions(cuestionario);
  const respondidas = Object.keys(respuestasSeleccionadas).length;
  const currentProcess = procesosFiltrados.find((x) => x.proceso._id === procesoSeleccionado);
  const currentAnswered = preguntas.filter((q) => respuestasSeleccionadas[getQuestionKey(q)]).length;

  if (loading) return <LoadingScreen />;

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: COLORS.background,
          p: 3,
          pt: { xs: 10, md: 4 },
          fontFamily: FONT_FAMILY,
        }}
      >
        <Alert severity="error" sx={{ borderRadius: 4 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!cuestionario) return <LoadingScreen />;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: COLORS.background,
        color: COLORS.text,
        pt: { xs: 9, md: 2.5 },
        pb: { xs: 12, md: 4 },
        fontFamily: FONT_FAMILY,
      }}
    >
      <Box sx={{ px: { xs: 1.5, md: 3 }, maxWidth: "1920px", mx: "auto" }}>
        <TopBar
          progreso={progreso}
          respondidas={respondidas}
          totalPreguntas={totalPreguntas}
          isCompleted={isCompleted}
          isLoadingFinalizar={isLoadingFinalizar}
          lastSavedAt={lastSavedAt}
          cuestionarioId={cuestionarioId}
          onFinish={finalizarCuestionario}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "318px minmax(0, 1fr)" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <ProcessSidebar
            procesosFiltrados={procesosFiltrados}
            procesoSeleccionado={procesoSeleccionado}
            respuestasSeleccionadas={respuestasSeleccionadas}
            nivelSeleccionado={nivelSeleccionado}
            searchProceso={searchProceso}
            onChangeNivel={setNivelSeleccionado}
            onSearch={setSearchProceso}
            onSelectProceso={(id) => seleccionarProceso(id, procesosFiltrados)}
          />

          <QuestionPanel
            currentProcess={currentProcess}
            preguntas={preguntas}
            respuestasSeleccionadas={respuestasSeleccionadas}
            isCompleted={isCompleted}
            savingQuestionId={savingQuestionId}
            currentAnswered={currentAnswered}
            onSelectAnswer={manejarSeleccionRespuesta}
            onOpenHelp={abrirDialogoAyuda}
          />
        </Box>
      </Box>

      <HelpDialog open={dialogoAbierto} text={ayudaSeleccionada} onClose={cerrarDialogoAyuda} />
    </Box>
  );
};

const TopBar = ({
  progreso,
  respondidas,
  totalPreguntas,
  isCompleted,
  isLoadingFinalizar,
  lastSavedAt,
  cuestionarioId,
  onFinish,
}) => (
  <Paper
    elevation={0}
    sx={{
      ...cardSx,
      p: { xs: 1.35, md: 1.65 },
      mb: 2,
      position: "sticky",
      top: { xs: 72, md: 12 },
      zIndex: 30,
      bgcolor: "rgba(255,255,255,0.92)",
      backdropFilter: "blur(18px)",
    }}
  >
    <Stack spacing={1.35}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1.4}
      >
        <Box sx={{ minWidth: 0 }}>
          <Breadcrumbs
            separator={<NavigateNextRoundedIcon fontSize="small" sx={{ color: COLORS.muted }} />}
            aria-label="breadcrumb"
            sx={{ mb: 0.45 }}
          >
            <MuiLink
              component={RouterLink}
              to="/mis-autoevaluaciones"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: COLORS.primary,
                fontWeight: TYPO.meta,
                fontSize: "0.76rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              <HomeRoundedIcon fontSize="small" sx={{ mr: 0.5 }} />
              Dashboard
            </MuiLink>

            <Typography
              sx={{
                color: COLORS.muted,
                fontWeight: TYPO.meta,
                fontSize: "0.76rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              Cuestionario
            </Typography>
          </Breadcrumbs>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography
              sx={{
                color: COLORS.textStrong,
                fontWeight: TYPO.pageTitle,
                fontSize: { xs: "1.16rem", md: "1.38rem" },
                letterSpacing: "-0.04em",
                lineHeight: 1.15,
                fontFamily: FONT_FAMILY,
              }}
            >
              Autoevaluación de procesos
            </Typography>

            {isCompleted && (
              <Chip
                icon={<VerifiedRoundedIcon />}
                label="Completada"
                size="small"
                sx={{
                  height: 24,
                  bgcolor: COLORS.successSoft,
                  color: COLORS.success,
                  fontWeight: TYPO.chip,
                  fontSize: "0.68rem",
                  border: `1px solid ${COLORS.success}22`,
                  fontFamily: FONT_FAMILY,

                  "& .MuiChip-icon": {
                    color: COLORS.success,
                  },
                }}
              />
            )}
          </Stack>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
          <Button
            component={RouterLink}
            to={`/instrucciones/${cuestionarioId}`}
            variant="outlined"
            startIcon={<TipsAndUpdatesRoundedIcon sx={{ fontSize: 18 }} />}
            sx={secondaryButtonSx}
          >
            Instrucciones
          </Button>

          <Button variant="contained" onClick={onFinish} disabled={isLoadingFinalizar} sx={primaryButtonSx}>
            {isLoadingFinalizar ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} color="inherit" />
                <span>Guardando...</span>
              </Stack>
            ) : isCompleted ? (
              "Finalizar revisión"
            ) : (
              "Finalizar"
            )}
          </Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.3} alignItems={{ xs: "stretch", md: "center" }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.55 }}>
            <Typography
              sx={{
                color: COLORS.muted,
                fontWeight: TYPO.meta,
                fontSize: "0.72rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              Progreso general
            </Typography>

            <Typography
              sx={{
                color: COLORS.textStrong,
                fontWeight: TYPO.sectionTitle,
                fontSize: "0.72rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              {progreso}%
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={progreso || 0}
            sx={{
              height: 7,
              borderRadius: 99,
              bgcolor: "rgba(15,36,56,0.08)",

              "& .MuiLinearProgress-bar": {
                borderRadius: 99,
                background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.accent})`,
              },
            }}
          />
        </Box>

        <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            label={`${respondidas}/${totalPreguntas} respondidas`}
            size="small"
            sx={{
              height: 25,
              bgcolor: COLORS.primarySoft,
              color: COLORS.primary,
              fontWeight: TYPO.chip,
              fontSize: "0.68rem",
              border: `1px solid ${COLORS.primary}22`,
              fontFamily: FONT_FAMILY,
            }}
          />

          <Chip
            icon={<CheckCircleRoundedIcon />}
            label={lastSavedAt ? "Guardado" : "Autoguardado"}
            size="small"
            sx={{
              height: 25,
              bgcolor: COLORS.successSoft,
              color: COLORS.success,
              fontWeight: TYPO.chip,
              fontSize: "0.68rem",
              border: `1px solid ${COLORS.success}22`,
              fontFamily: FONT_FAMILY,

              "& .MuiChip-icon": {
                color: COLORS.success,
              },
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  </Paper>
);

const ProcessSidebar = ({
  procesosFiltrados,
  procesoSeleccionado,
  respuestasSeleccionadas,
  nivelSeleccionado,
  searchProceso,
  onChangeNivel,
  onSearch,
  onSelectProceso,
}) => (
  <Paper
    elevation={0}
    sx={{
      ...cardSx,
      p: 1.35,
      position: { lg: "sticky" },
      top: { lg: 154 },
      maxHeight: { lg: "calc(100vh - 176px)" },
      display: "flex",
      flexDirection: "column",
      minHeight: { xs: "auto", lg: 520 },
    }}
  >
    <Stack spacing={1.15} sx={{ mb: 1.15 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography
            sx={{
              color: COLORS.textStrong,
              fontWeight: TYPO.sectionTitle,
              fontSize: "0.92rem",
              letterSpacing: "-0.02em",
              fontFamily: FONT_FAMILY,
            }}
          >
            Procesos
          </Typography>

          <Typography
            sx={{
              color: COLORS.muted,
              fontWeight: TYPO.body,
              fontSize: "0.7rem",
              mt: 0.2,
              fontFamily: FONT_FAMILY,
            }}
          >
            Selecciona un bloque para responder
          </Typography>
        </Box>

        <Chip
          size="small"
          label={procesosFiltrados.length}
          sx={{
            height: 23,
            bgcolor: COLORS.surfaceSoft,
            color: COLORS.muted,
            fontWeight: TYPO.chip,
            fontSize: "0.66rem",
            border: `1px solid ${COLORS.border}`,
            fontFamily: FONT_FAMILY,
          }}
        />
      </Stack>

      <TextField
        value={searchProceso}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Buscar proceso..."
        size="small"
        sx={{
          "& .MuiOutlinedInput-root": {
            height: 40,
            borderRadius: "13px",
            bgcolor: COLORS.surfaceSoft,
            color: COLORS.text,
            fontFamily: FONT_FAMILY,

            "& fieldset": {
              borderColor: COLORS.border,
            },

            "&:hover fieldset": {
              borderColor: COLORS.primary,
            },

            "&.Mui-focused fieldset": {
              borderColor: COLORS.primary,
            },
          },

          "& .MuiInputBase-input": {
            fontWeight: TYPO.body,
            fontSize: "0.76rem",
            fontFamily: FONT_FAMILY,
          },

          "& input::placeholder": {
            color: COLORS.muted,
            opacity: 1,
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon sx={{ color: COLORS.muted, fontSize: 18 }} />
            </InputAdornment>
          ),
          endAdornment: searchProceso ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => onSearch("")}>
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      <FormControl fullWidth size="small">
        <Select
          value={nivelSeleccionado}
          onChange={(e) => onChangeNivel(e.target.value)}
          sx={{
            height: 40,
            borderRadius: "13px",
            bgcolor: COLORS.surfaceSoft,
            color: COLORS.text,
            fontWeight: TYPO.bodyStrong,
            fontSize: "0.74rem",
            fontFamily: FONT_FAMILY,

            "& fieldset": {
              borderColor: COLORS.border,
            },

            "&:hover fieldset": {
              borderColor: COLORS.primary,
            },

            "&.Mui-focused fieldset": {
              borderColor: COLORS.primary,
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                bgcolor: COLORS.surface,
                color: COLORS.text,
                border: `1px solid ${COLORS.border}`,
                borderRadius: "14px",
                boxShadow: "0 18px 44px rgba(15,36,56,0.12)",
                mt: 0.8,

                "& .MuiMenuItem-root": {
                  fontSize: "0.76rem",
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                },
              },
            },
          }}
        >
          {NIVEL_OPTIONS.map((nivel) => (
            <MenuItem key={nivel} value={nivel}>
              {nivel === "Todos los niveles" ? nivel : `Nivel ${nivel}`}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>

    <Divider sx={{ borderColor: COLORS.border, mb: 1.15 }} />

    <Box
      sx={{
        overflowY: "auto",
        pr: 0.4,
        flex: 1,

        "&::-webkit-scrollbar": {
          width: 6,
        },

        "&::-webkit-scrollbar-thumb": {
          bgcolor: "rgba(100,116,139,0.22)",
          borderRadius: 99,
        },
      }}
    >
      {procesosFiltrados.length === 0 ? (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography
            sx={{
              color: COLORS.muted,
              fontWeight: TYPO.bodyStrong,
              fontSize: "0.78rem",
              fontFamily: FONT_FAMILY,
            }}
          >
            No hay procesos
          </Typography>
        </Box>
      ) : (
        <Stack spacing={0.8}>
          {procesosFiltrados.map((p) => (
            <ProcessItem
              key={p.proceso._id}
              item={p}
              selected={p.proceso._id === procesoSeleccionado}
              respuestasSeleccionadas={respuestasSeleccionadas}
              onClick={() => onSelectProceso(p.proceso._id)}
            />
          ))}
        </Stack>
      )}
    </Box>
  </Paper>
);

const ProcessItem = ({ item, selected, respuestasSeleccionadas, onClick }) => {
  const answered = item.preguntas.filter((q) => respuestasSeleccionadas[getQuestionKey(q)]).length;
  const total = item.preguntas.length;
  const complete = total > 0 && answered === total;
  const progress = total ? Math.round((answered / total) * 100) : 0;
  const title = item.proceso.abreviatura || item.proceso.nombre || "Proceso";
  const subtitle = item.proceso.nombre || "Preguntas del proceso";

  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.05,
        borderRadius: "16px",
        cursor: "pointer",
        bgcolor: selected ? COLORS.primarySoft : COLORS.surface,
        border: selected
          ? "1px solid rgba(30,75,129,0.28)"
          : `1px solid ${COLORS.border}`,
        boxShadow: selected ? "0 10px 24px rgba(30,75,129,0.08)" : "none",
        transition: "all 180ms ease",

        "&:hover": {
          bgcolor: selected ? COLORS.primarySoft : COLORS.surfaceSoft,
          borderColor: "rgba(30,75,129,0.22)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: complete
              ? COLORS.successSoft
              : selected
                ? COLORS.surface
                : COLORS.surfaceSoft,
            color: complete ? COLORS.success : selected ? COLORS.primary : COLORS.muted,
            border: `1px solid ${complete ? COLORS.success + "22" : COLORS.border}`,
            flexShrink: 0,
          }}
        >
          {complete ? <DoneRoundedIcon fontSize="small" /> : <DescriptionIconFallback text={title} />}
        </Avatar>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.8} alignItems="center" justifyContent="space-between">
            <Typography
              noWrap
              sx={{
                color: COLORS.textStrong,
                fontWeight: selected ? TYPO.selectedItem : TYPO.bodyStrong,
                fontSize: "0.78rem",
                fontFamily: FONT_FAMILY,
              }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                color: complete ? COLORS.success : COLORS.muted,
                fontWeight: TYPO.meta,
                fontSize: "0.66rem",
                flexShrink: 0,
                fontFamily: FONT_FAMILY,
              }}
            >
              {answered}/{total}
            </Typography>
          </Stack>

          <Typography
            sx={{
              color: COLORS.muted,
              fontWeight: TYPO.body,
              fontSize: "0.68rem",
              lineHeight: 1.35,
              mt: 0.25,
              fontFamily: FONT_FAMILY,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {subtitle}
          </Typography>

          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mt: 0.8,
              height: 5,
              borderRadius: 99,
              bgcolor: "rgba(15,36,56,0.08)",

              "& .MuiLinearProgress-bar": {
                borderRadius: 99,
                bgcolor: complete ? COLORS.success : COLORS.primary,
              },
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
};

const DescriptionIconFallback = ({ text }) => (
  <Typography
    sx={{
      fontSize: "0.68rem",
      fontWeight: TYPO.sectionTitle,
      fontFamily: FONT_FAMILY,
    }}
  >
    {String(text || "P").charAt(0).toUpperCase()}
  </Typography>
);

const QuestionPanel = ({
  currentProcess,
  preguntas,
  respuestasSeleccionadas,
  isCompleted,
  savingQuestionId,
  currentAnswered,
  onSelectAnswer,
  onOpenHelp,
}) => {
  const processName = currentProcess?.proceso?.nombre || "Seleccione un proceso";
  const processShort = currentProcess?.proceso?.abreviatura;
  const total = preguntas.length;
  const currentProgress = total ? Math.round((currentAnswered / total) * 100) : 0;

  return (
    <Paper elevation={0} sx={{ ...cardSx, overflow: "hidden" }}>
      <Box
        sx={{
          px: { xs: 1.7, md: 2.4 },
          py: 1.9,
          borderBottom: `1px solid ${COLORS.border}`,
          bgcolor: "rgba(255,255,255,0.86)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Box sx={{ minWidth: 0 }}>
            {processShort && (
              <Chip
                label={processShort}
                size="small"
                sx={{
                  mb: 0.75,
                  height: 23,
                  bgcolor: COLORS.accentSoft,
                  color: COLORS.accent,
                  fontWeight: TYPO.chip,
                  fontSize: "0.68rem",
                  border: `1px solid ${COLORS.accent}22`,
                  fontFamily: FONT_FAMILY,
                }}
              />
            )}

            <Typography
              sx={{
                color: COLORS.textStrong,
                fontWeight: TYPO.pageTitle,
                fontSize: { xs: "1.08rem", md: "1.26rem" },
                letterSpacing: "-0.035em",
                lineHeight: 1.22,
                fontFamily: FONT_FAMILY,
              }}
            >
              {processName}
            </Typography>

            <Typography
              sx={{
                color: COLORS.muted,
                fontWeight: TYPO.body,
                fontSize: "0.78rem",
                mt: 0.5,
                lineHeight: 1.55,
                fontFamily: FONT_FAMILY,
              }}
            >
              Responde cada pregunta según la situación real de tu organización.
            </Typography>
          </Box>

          <Box sx={{ minWidth: { xs: "100%", md: 220 } }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.55 }}>
              <Typography
                sx={{
                  color: COLORS.muted,
                  fontWeight: TYPO.meta,
                  fontSize: "0.72rem",
                  fontFamily: FONT_FAMILY,
                }}
              >
                Proceso
              </Typography>

              <Typography
                sx={{
                  color: COLORS.textStrong,
                  fontWeight: TYPO.sectionTitle,
                  fontSize: "0.72rem",
                  fontFamily: FONT_FAMILY,
                }}
              >
                {currentAnswered}/{total}
              </Typography>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={currentProgress}
              sx={{
                height: 7,
                borderRadius: 99,
                bgcolor: "rgba(15,36,56,0.08)",

                "& .MuiLinearProgress-bar": {
                  borderRadius: 99,
                  bgcolor: currentProgress === 100 ? COLORS.success : COLORS.primary,
                },
              }}
            />
          </Box>
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 1.45, md: 2.2 } }}>
        {preguntas.length === 0 ? (
          <EmptyQuestionState />
        ) : (
          <Stack spacing={1.45}>
            {preguntas.map((pregunta, index) => (
              <QuestionCard
                key={pregunta.pregunta}
                pregunta={pregunta}
                index={index}
                selectedAnswer={respuestasSeleccionadas[pregunta.pregunta]}
                disabled={isCompleted}
                saving={savingQuestionId === pregunta.pregunta}
                onSelectAnswer={onSelectAnswer}
                onOpenHelp={onOpenHelp}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

const QuestionCard = ({
  pregunta,
  index,
  selectedAnswer,
  disabled,
  saving,
  onSelectAnswer,
  onOpenHelp,
}) => {
  const answered = Boolean(selectedAnswer);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.45, md: 1.8 },
        borderRadius: "20px",
        border: answered
          ? "1px solid rgba(20,166,122,0.22)"
          : `1px solid ${COLORS.border}`,
        bgcolor: answered ? "rgba(234,251,246,0.42)" : COLORS.surface,
        boxShadow: "0 10px 28px rgba(15,36,56,0.035)",
        transition: "all 180ms ease",

        "&:hover": {
          boxShadow: COLORS.hoverShadow,
          borderColor: answered ? "rgba(20,166,122,0.30)" : "rgba(30,75,129,0.18)",
        },
      }}
    >
      <Stack spacing={1.35}>
        <Stack direction="row" spacing={1.1} alignItems="flex-start">
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: answered ? COLORS.successSoft : COLORS.primarySoft,
              color: answered ? COLORS.success : COLORS.primary,
              border: `1px solid ${answered ? COLORS.success + "22" : COLORS.primary + "22"}`,
              fontWeight: TYPO.sectionTitle,
              fontSize: "0.78rem",
              flexShrink: 0,
              fontFamily: FONT_FAMILY,
            }}
          >
            {answered ? <DoneRoundedIcon fontSize="small" /> : index + 1}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Typography
                sx={{
                  color: COLORS.textStrong,
                  fontWeight: 680,
                  fontSize: "0.88rem",
                  lineHeight: 1.58,
                  flex: 1,
                  fontFamily: FONT_FAMILY,
                }}
              >
                {pregunta.descripcion}
              </Typography>

              {pregunta.ayuda && (
                <Tooltip title="Ver ayuda">
                  <IconButton
                    size="small"
                    onClick={() => onOpenHelp(pregunta.ayuda)}
                    sx={{
                      width: 32,
                      height: 32,
                      color: COLORS.accent,
                      bgcolor: COLORS.accentSoft,
                      border: `1px solid ${COLORS.accent}22`,
                      flexShrink: 0,

                      "&:hover": {
                        bgcolor: "rgba(211,125,54,0.16)",
                      },
                    }}
                  >
                    <HelpOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            {saving && (
              <Typography
                sx={{
                  color: COLORS.muted,
                  fontWeight: TYPO.meta,
                  fontSize: "0.7rem",
                  mt: 0.65,
                  fontFamily: FONT_FAMILY,
                }}
              >
                Guardando respuesta...
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack spacing={0.85}>
          {pregunta.respuestas.map((respuesta) => {
            const selected = selectedAnswer === respuesta._id;

            return (
              <AnswerOption
                key={respuesta._id}
                respuesta={respuesta}
                selected={selected}
                disabled={disabled}
                onClick={() => onSelectAnswer(pregunta.pregunta, respuesta._id)}
              />
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};

const AnswerOption = ({ respuesta, selected, disabled, onClick }) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      px: 1.25,
      py: 1,
      borderRadius: "15px",
      border: selected
        ? "1px solid rgba(30,75,129,0.34)"
        : `1px solid ${COLORS.border}`,
      bgcolor: selected ? COLORS.primarySoft : COLORS.surfaceSoft,
      cursor: disabled ? "default" : "pointer",
      opacity: disabled && !selected ? 0.72 : 1,
      transition: "all 160ms ease",

      "&:hover": disabled
        ? {}
        : {
            borderColor: selected
              ? "rgba(30,75,129,0.34)"
              : "rgba(30,75,129,0.22)",
            bgcolor: selected ? COLORS.primarySoft : COLORS.surface,
            transform: "translateY(-1px)",
          },
    }}
  >
    <Stack direction="row" spacing={1.1} alignItems="flex-start">
      <Box
        sx={{
          mt: "2px",
          width: 23,
          height: 23,
          borderRadius: "9px",
          display: "grid",
          placeItems: "center",
          bgcolor: selected ? COLORS.primary : COLORS.surface,
          color: selected ? COLORS.surface : COLORS.mutedSoft,
          border: selected ? "none" : `1px solid ${COLORS.borderStrong}`,
          flexShrink: 0,
        }}
      >
        {selected ? (
          <DoneRoundedIcon sx={{ fontSize: 16 }} />
        ) : (
          <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 16 }} />
        )}
      </Box>

      <Typography
        sx={{
          color: selected ? COLORS.textStrong : COLORS.text,
          fontWeight: selected ? 700 : TYPO.body,
          fontSize: "0.82rem",
          lineHeight: 1.55,
          fontFamily: FONT_FAMILY,
        }}
      >
        {respuesta.descripcion}
      </Typography>
    </Stack>
  </Box>
);

const HelpDialog = ({ open, text, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: "24px",
        boxShadow: "0 28px 70px rgba(15,36,56,0.18)",
      },
    }}
  >
    <DialogContent sx={{ p: 0 }}>
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${COLORS.border}`,
          bgcolor: COLORS.surfaceSoft,
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.1} alignItems="center">
            <Avatar
              sx={{
                width: 38,
                height: 38,
                bgcolor: COLORS.accentSoft,
                color: COLORS.accent,
              }}
            >
              <InfoRoundedIcon />
            </Avatar>

            <Box>
              <Typography
                sx={{
                  color: COLORS.textStrong,
                  fontWeight: TYPO.sectionTitle,
                  fontSize: "0.96rem",
                  fontFamily: FONT_FAMILY,
                }}
              >
                Ayuda para responder
              </Typography>

              <Typography
                sx={{
                  color: COLORS.muted,
                  fontWeight: TYPO.body,
                  fontSize: "0.74rem",
                  fontFamily: FONT_FAMILY,
                }}
              >
                Consulta esta explicación antes de seleccionar una respuesta.
              </Typography>
            </Box>
          </Stack>

          <IconButton onClick={onClose} sx={{ color: COLORS.muted }}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ p: 2.3 }}>
        <Typography
          sx={{
            color: COLORS.text,
            fontSize: "0.86rem",
            lineHeight: 1.75,
            fontWeight: TYPO.body,
            whiteSpace: "pre-line",
            fontFamily: FONT_FAMILY,
          }}
        >
          {text}
        </Typography>
      </Box>
    </DialogContent>
  </Dialog>
);

const EmptyQuestionState = () => (
  <Box sx={{ py: 8, textAlign: "center" }}>
    <Typography
      sx={{
        color: COLORS.textStrong,
        fontWeight: TYPO.sectionTitle,
        fontSize: "1.05rem",
        fontFamily: FONT_FAMILY,
      }}
    >
      No hay preguntas disponibles
    </Typography>

    <Typography
      sx={{
        color: COLORS.muted,
        fontWeight: TYPO.body,
        mt: 0.8,
        fontSize: "0.82rem",
        fontFamily: FONT_FAMILY,
      }}
    >
      Selecciona otro proceso o cambia el filtro de nivel.
    </Typography>
  </Box>
);

const LoadingScreen = () => (
  <Box
    sx={{
      minHeight: "100vh",
      background: COLORS.background,
      display: "grid",
      placeItems: "center",
      p: 3,
      fontFamily: FONT_FAMILY,
    }}
  >
    <Paper elevation={0} sx={{ ...cardSx, p: 4, textAlign: "center", maxWidth: 420 }}>
      <CircularProgress sx={{ color: COLORS.primary, mb: 2 }} />

      <Typography
        sx={{
          color: COLORS.textStrong,
          fontWeight: TYPO.sectionTitle,
          fontSize: "1.05rem",
          fontFamily: FONT_FAMILY,
        }}
      >
        Cargando cuestionario
      </Typography>

      <Typography
        sx={{
          color: COLORS.muted,
          fontWeight: TYPO.body,
          mt: 0.8,
          fontSize: "0.82rem",
          lineHeight: 1.6,
          fontFamily: FONT_FAMILY,
        }}
      >
        Estamos preparando tus preguntas y respuestas guardadas.
      </Typography>
    </Paper>
  </Box>
);

export default CuestionarioPage;
